import { Suspense, lazy, ReactNode, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { WhiteLabelProvider } from "@/contexts/WhiteLabelContext";
import { DemoProvider } from "@/contexts/DemoContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { AuthDialog } from "@/components/auth/AuthDialog";
import ErrorBoundary from "@/components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

// Inline loading spinner - no external import
const Loading = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Inline error fallback
const PageError = ({ error }: { error?: string }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
    <h2 className="text-xl font-semibold mb-2">Page failed to load</h2>
    {error && <p className="text-muted-foreground mb-4 text-sm">{error}</p>}
    <button 
      onClick={() => window.location.reload()} 
      className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
    >
      Reload Page
    </button>
  </div>
);

// Lazy load ALL pages - each handles its own layout
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const WorkspacePage = lazy(() => import("@/pages/WorkspacePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CompetitiveIntelligencePage = lazy(() => import("@/pages/CompetitiveIntelligencePage"));
const EconomicIntelligencePage = lazy(() => import("@/pages/EconomicIntelligencePage"));
const KnowledgeBasePage = lazy(() => import("@/pages/KnowledgeBasePage"));
const PlatformIntelligencePage = lazy(() => import("@/pages/PlatformIntelligencePage"));
const ProjectsListPage = lazy(() => import("@/pages/ProjectsListPage"));
const ProjectWorkspacePage = lazy(() => import("@/pages/ProjectWorkspacePage"));
const UserSettingsPage = lazy(() => import("@/pages/UserSettingsPage"));
const AdminOrganizationSetupPage = lazy(() => import("@/pages/AdminOrganizationSetupPage"));
const SuperAdminPage = lazy(() => import("@/pages/SuperAdminPage"));
const DiagnosticPage = lazy(() => import("@/pages/DiagnosticPage"));
const CompanyHubPage = lazy(() => import("@/pages/CompanyHubPage"));
const StrategyStudioPage = lazy(() => import("@/pages/StrategyStudioPage"));
const StrategicRadarPage = lazy(() => import("@/pages/StrategicRadarPage"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const ExecutiveBriefingsPage = lazy(() => import("@/pages/ExecutiveBriefingsPage"));
const StrategicPortfolioPage = lazy(() => import("@/pages/StrategicPortfolioPage"));
const CalibrationPage = lazy(() => import("@/pages/CalibrationPage"));
const PublicCalibrationPage = lazy(() => import("@/pages/PublicCalibrationPage"));
const JourneysPage = lazy(() => import("@/pages/JourneysPage"));
const JourneyDetailPage = lazy(() => import("@/pages/JourneyDetailPage"));
const SSOConfigPage = lazy(() => import("@/pages/SSOConfigPage"));
const APIDocs = lazy(() => import("@/pages/APIDocs"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const ProductShowcasePage = lazy(() => import("@/pages/ProductShowcasePage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const DealRoomsPage = lazy(() => import("@/pages/DealRoomsPage"));
const DealRoomDetailPage = lazy(() => import("@/pages/DealRoomDetailPage"));
const FundsPage = lazy(() => import("@/pages/FundsPage"));
const FundDetailPage = lazy(() => import("@/pages/FundDetailPage"));
const ExitPlanningPage = lazy(() => import("@/pages/ExitPlanningPage"));
const CoInvestmentPage = lazy(() => import("@/pages/CoInvestmentPage"));
const PlatformMetricsPage = lazy(() => import("@/pages/PlatformMetricsPage"));
const DailyBriefingPage = lazy(() => import("@/pages/DailyBriefingPage"));
const PortfolioDashboardPage = lazy(() => import("@/pages/PortfolioDashboardPage"));
const PortfolioMonitorPage = lazy(() => import("@/pages/PortfolioMonitorPage"));
const ThreadsPage = lazy(() => import("@/pages/ThreadsPage"));
const TaiwanStraitIntelligence = lazy(() => import("@/pages/TaiwanStraitIntelligence"));
const ApiKeysPage = lazy(() => import("@/pages/ApiKeysPage"));

// Protected route wrapper - checks auth, shows dialog if not logged in
function ProtectedContent({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (loading) return <Loading />;
  
  if (dismissed && !user) return <Navigate to="/" replace />;

  if (!user) {
    return (
      <AuthDialog 
        open={true} 
        onOpenChange={(open) => { if (!open) setDismissed(true); }} 
      />
    );
  }

  return <>{children}</>;
}

// Super Admin route wrapper - checks auth AND super_admin role
function SuperAdminContent({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin, loading: roleLoading } = useIsSuperAdmin();
  const [dismissed, setDismissed] = useState(false);

  if (authLoading || roleLoading) return <Loading />;
  
  if (dismissed && !user) return <Navigate to="/" replace />;

  if (!user) {
    return (
      <AuthDialog 
        open={true} 
        onOpenChange={(open) => { if (!open) setDismissed(true); }} 
      />
    );
  }

  // User is authenticated but not a super admin - redirect to dashboard
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Routes component
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <Suspense fallback={<Loading />}><LandingPage /></Suspense>
      } />
      {/* Public Security Trust Center */}
      <Route path="/security" element={
        <Suspense fallback={<Loading />}><SecurityPage /></Suspense>
      } />
      {/* Public Calibration Proof Page */}
      <Route path="/proof" element={
        <Suspense fallback={<Loading />}><PublicCalibrationPage /></Suspense>
      } />
      {/* Legacy /landing redirect */}
      <Route path="/landing" element={<Navigate to="/" replace />} />

      {/* Protected routes - each page handles its own AppLayout */}
      <Route path="/dashboard" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><DashboardPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Workspace consolidated into Threads (M3) */}
      <Route path="/workspace" element={<Navigate to="/threads" replace />} />
      {/* Bayesian Diagnostic Engine */}
      <Route path="/diagnostic" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><DiagnosticPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Company Hub */}
      <Route path="/company" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><CompanyHubPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Strategy Studio */}
      <Route path="/studio" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><StrategyStudioPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Enterprise: Strategic Radar */}
      <Route path="/strategic-radar" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><StrategicRadarPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Enterprise: Integration Hub */}
      <Route path="/integrations" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><IntegrationsPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Enterprise: Executive Briefings */}
      <Route path="/briefings" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ExecutiveBriefingsPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Strategic Portfolio - Graphical Testing */}
      <Route path="/portfolio" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><StrategicPortfolioPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Calibration Engine */}
      <Route path="/calibration" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><CalibrationPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Journeys */}
      <Route path="/journeys" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><JourneysPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/journeys/:id" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><JourneyDetailPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Legacy route redirects */}
      <Route path="/chat" element={<Navigate to="/threads" replace />} />
      <Route path="/socratic" element={<Navigate to="/diagnostic" replace />} />
      <Route path="/competitive" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><CompetitiveIntelligencePage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/economic-intelligence" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><EconomicIntelligencePage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/knowledge-base" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><KnowledgeBasePage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/platform-intelligence" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><PlatformIntelligencePage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/projects" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ProjectsListPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/projects/:projectId" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ProjectWorkspacePage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/threads" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ThreadsPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/settings" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><UserSettingsPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/settings/api-keys" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ApiKeysPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/intelligence/taiwan-strait" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><TaiwanStraitIntelligence /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/admin/organization" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><AdminOrganizationSetupPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/super-admin" element={
        <SuperAdminContent>
          <Suspense fallback={<Loading />}><SuperAdminPage /></Suspense>
        </SuperAdminContent>
      } />
      <Route path="/sso-config" element={
        <SuperAdminContent>
          <Suspense fallback={<Loading />}><SSOConfigPage /></Suspense>
        </SuperAdminContent>
      } />
      <Route path="/admin/platform-metrics" element={
        <SuperAdminContent>
          <Suspense fallback={<Loading />}><PlatformMetricsPage /></Suspense>
        </SuperAdminContent>
      } />
      <Route path="/api-docs" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><APIDocs /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/billing" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><BillingPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/about" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ProductShowcasePage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/case-studies" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><CaseStudiesPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Daily Briefing - standalone mobile-optimized view (no AppLayout) */}
      <Route path="/briefing" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><DailyBriefingPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Deal Rooms */}
      <Route path="/deal-rooms" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><DealRoomsPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/deal-room/:roomId" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><DealRoomDetailPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Fund Management */}
      <Route path="/funds" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><FundsPage /></Suspense>
        </ProtectedContent>
      } />
      <Route path="/fund/:fundId" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><FundDetailPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Exit Planning */}
      <Route path="/exit-planning" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><ExitPlanningPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Co-Investment */}
      <Route path="/co-investment" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><CoInvestmentPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Portfolio Dashboard */}
      <Route path="/portfolio-dashboard" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><PortfolioDashboardPage /></Suspense>
        </ProtectedContent>
      } />
      {/* Portfolio Monitoring Dashboard */}
      <Route path="/portfolio-monitor" element={
        <ProtectedContent>
          <Suspense fallback={<Loading />}><PortfolioMonitorPage /></Suspense>
        </ProtectedContent>
      } />

      {/* 404 */}
      <Route path="*" element={
        <Suspense fallback={<Loading />}><NotFound /></Suspense>
      } />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <AuthProvider>
        <OrganizationProvider>
          <WhiteLabelProvider>
            <DemoProvider>
              <TooltipProvider>
                <BrowserRouter>
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </BrowserRouter>
                <Toaster />
                <Sonner />
              </TooltipProvider>
            </DemoProvider>
          </WhiteLabelProvider>
        </OrganizationProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
